interface RouteInfo {
  to: { location: { pathname: string } };
  from: { location: { pathname: string } };
}

interface User {
  roles: { authority: string }[];
}

interface NextFunction {
  (): void;
  redirect(path: string): void;
}

const isComingFromIntro = (from: RouteInfo["from"]): boolean =>
  from.location.pathname === "/intro";

const isNavigatingToHomeOnMobile = (
  isMobileScreen: boolean,
  to: RouteInfo["to"],
  from: RouteInfo["from"],
): boolean =>
  isMobileScreen &&
  to.location.pathname === "/" &&
  from.location.pathname === "/";

const isAdminPath = (from: RouteInfo["from"]): boolean =>
  from.location.pathname.includes("admin");

const hasRole = (user: User | null, role: string): boolean =>
  user?.roles.some((r) => r.authority === role) ?? false;

const isAdmin = (user: User | null): boolean => hasRole(user, "ADMIN");
const isDev = (user: User | null): boolean => hasRole(user, "DEV");

const handleIntroNavigation = (next: NextFunction): void => next();

const handleMobileHomeNavigation = (next: NextFunction): void =>
  next.redirect("/intro");

const handleAdminNavigation = (
  from: RouteInfo["from"],
  next: NextFunction,
): void => (isAdminPath(from) ? next() : next.redirect("/admin/home"));

const handleDevNavigation = (
  from: RouteInfo["from"],
  next: NextFunction,
): void => (isAdminPath(from) ? next.redirect("/home") : next());

const handleDefaultNavigation = (next: NextFunction): void => next();

const createDiscriminatorEnv =
  (isMobileScreen: boolean, user: User | null) =>
  (to: any, from: any, next: any): any => {
    if (isComingFromIntro(from)) {
      return handleIntroNavigation(next);
    }

    if (isNavigatingToHomeOnMobile(isMobileScreen, to, from)) {
      return handleMobileHomeNavigation(next);
    }

    if (!user) return handleDefaultNavigation(next);

    if (isAdmin(user)) {
      return handleAdminNavigation(from, next);
    }

    if (isDev(user)) {
      return handleDevNavigation(from, next);
    }

    return handleDefaultNavigation(next);
  };

export default createDiscriminatorEnv;
